import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Typography, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, Alert, Collapse, IconButton, Button
} from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import moment from "moment";
import api from "../../services/api";

const DAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Mapa de calor dia-da-semana x hora, baseado nos ultimos 90 dias
// (peakHours vindo do backend, sempre nesse periodo fixo independente do
// filtro de data da tela - um recorte curto nao tem volume suficiente
// pra mostrar um padrao real). extract(dow from ...) do Postgres
// retorna 0=domingo..6=sabado, mesma ordem de DAYS_SHORT. Movido do
// Painel de TV pra ca 2026-08-18 - e' leitura/analise, nao "ao vivo".
const interpolateChannel = (start, end, t) => Math.round(start + (end - start) * t);

const heatCellColor = (intensity) => {
  if (intensity === 0) return "#f0f0f0";
  const start = [253, 224, 200];
  const end = [190, 20, 25];
  const [r, g, b] = start.map((s, i) => interpolateChannel(s, end[i], intensity));
  return `rgb(${r}, ${g}, ${b})`;
};

const PeakHoursHeatmap = ({ data }) => {
  const grid = {};
  (data || []).forEach((d) => {
    grid[`${d.dayOfWeek}-${d.hour}`] = Number(d.count);
  });
  const values = (data || []).map((d) => Number(d.count));
  const maxCount = values.length ? Math.max(...values) : 1;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Dia da semana × hora do dia (atendimentos nos últimos 90 dias)</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "34px repeat(24, minmax(26px, 1fr))", gap: "3px", alignItems: "center" }}>
        <Box />
        {Array.from({ length: 24 }).map((_, h) => (
          <Typography key={h} variant="caption" align="center" sx={{ fontSize: 10, color: "text.secondary" }}>
            {h}h
          </Typography>
        ))}
        {DAYS_SHORT.map((day, dIdx) => (
          <React.Fragment key={day}>
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary" }}>{day}</Typography>
            {Array.from({ length: 24 }).map((_, h) => {
              const count = grid[`${dIdx}-${h}`] || 0;
              const intensity = maxCount > 0 ? count / maxCount : 0;
              return (
                <Box
                  key={h}
                  title={`${day} ${h}h: ${count} atendimento(s) nos últimos 90 dias`}
                  sx={{
                    height: 26,
                    borderRadius: "4px",
                    bgcolor: heatCellColor(intensity),
                    border: "1px solid",
                    borderColor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: intensity > 0.55 ? "#fff" : "#5c4636",
                      lineHeight: 1,
                    }}
                  >
                    {count || ""}
                  </Typography>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Menos atendimentos</Typography>
        <Box sx={{ display: "flex", gap: "3px" }}>
          {[0.05, 0.25, 0.5, 0.75, 1].map((intensity) => (
            <Box key={intensity} sx={{ width: 20, height: 12, borderRadius: "3px", bgcolor: heatCellColor(intensity) }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">Mais atendimentos</Typography>
      </Box>
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", fontWeight: 700, letterSpacing: 1, color: "text.secondary", mb: 1, mt: 1 }}
  >
    {children}
  </Typography>
);

const formatElapsed = (minutes) => {
  const m = Math.round(minutes || 0);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return h > 0 ? `${h}h ${rest}m` : `${rest}m`;
};

const AttendanceReportTab = ({ dateStart, dateEnd }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [expandedReason, setExpandedReason] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState({});

  const fetchSummary = async (contactId) => {
    setSummaryLoading((prev) => ({ ...prev, [contactId]: true }));
    try {
      const { data } = await api.get(`/dashboard/contact-summary/${contactId}`);
      setSummaries((prev) => ({ ...prev, [contactId]: data.summary || "Sem histórico suficiente pra resumir." }));
    } catch (err) {
      setSummaries((prev) => ({ ...prev, [contactId]: "Não foi possível gerar o resumo agora." }));
    } finally {
      setSummaryLoading((prev) => ({ ...prev, [contactId]: false }));
    }
  };

  const fetchReport = useCallback(async () => {
    if (!moment(dateStart, "YYYY-MM-DD", true).isValid() || !moment(dateEnd, "YYYY-MM-DD", true).isValid()) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/dashboard/attendance-report", {
        params: { dateFrom: dateStart, dateTo: dateEnd }
      });
      setReport(data);
      setExpandedContactId(null);
    } catch (err) {
      setError("Não foi possível carregar o relatório de atendimentos.");
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  }

  if (!report) return null;

  const {
    openTickets, recurrenceByContact, recurrenceByOrganization, reasonBreakdown, reasonContacts,
    recentClosedSummaries, peakHours
  } = report;
  const contactsByReason = (reasonContacts || []).reduce((acc, rc) => {
    if (!acc[rc.reason]) acc[rc.reason] = [];
    acc[rc.reason].push(rc);
    return acc;
  }, {});

  return (
    <Box>
      <SectionTitle>Atendimentos em aberto</SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: "hidden" }}>
        {openTickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Nenhum atendimento em aberto.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contato</TableCell>
                <TableCell>Atendente</TableCell>
                <TableCell>Fila</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Tempo decorrido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {openTickets.map((t) => (
                <TableRow key={t.ticketId} hover>
                  <TableCell>{t.contactName || t.contactNumber || "-"}</TableCell>
                  <TableCell>{t.userName || "-"}</TableCell>
                  <TableCell>{t.queueName || "-"}</TableCell>
                  <TableCell>{t.status === "pending" ? "Aguardando" : "Em atendimento"}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={formatElapsed(t.elapsedMinutes)}
                      color={t.elapsedMinutes > 60 ? "error" : t.elapsedMinutes > 20 ? "warning" : "default"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <RepeatIcon fontSize="small" color="action" />
        <SectionTitle>Clientes recorrentes (por contato)</SectionTitle>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: "hidden" }}>
        {recurrenceByContact.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Nenhum contato chamou mais de uma vez no período.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Contato</TableCell>
                <TableCell>Número</TableCell>
                <TableCell align="right">Chamados no período</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recurrenceByContact.map((c) => {
                const isExpanded = expandedContactId === c.contactId;
                return (
                  <React.Fragment key={c.contactId}>
                    <TableRow
                      hover
                      onClick={() => setExpandedContactId(isExpanded ? null : c.contactId)}
                      sx={{ cursor: "pointer", "& > *": { borderBottom: isExpanded ? "unset" : undefined } }}
                    >
                      <TableCell sx={{ width: 40 }}>
                        <IconButton size="small">
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{c.contactName || "-"}</TableCell>
                      <TableCell>{c.contactNumber || "-"}</TableCell>
                      <TableCell align="right"><strong>{c.ticketCount}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0 }} colSpan={4}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, py: 1.5, px: 1 }}>
                            {(c.reasons || []).map((r) => (
                              <Chip
                                key={r.reason}
                                size="small"
                                label={`${r.count}x ${r.reason}`}
                                color={r.reason === "Sem motivo registrado" ? "default" : "primary"}
                                variant={r.reason === "Sem motivo registrado" ? "outlined" : "filled"}
                              />
                            ))}
                          </Box>
                          <Box sx={{ px: 1, pb: 1.5 }}>
                            {!summaries[c.contactId] && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AutoAwesomeIcon fontSize="small" />}
                                disabled={!!summaryLoading[c.contactId]}
                                onClick={() => fetchSummary(c.contactId)}
                              >
                                {summaryLoading[c.contactId] ? "Gerando..." : "Resumo com IA"}
                              </Button>
                            )}
                            {summaries[c.contactId] && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 0.5 }}>
                                {summaries[c.contactId]}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon fontSize="small" color="action" />
        <SectionTitle>Empresas recorrentes (por CNPJ)</SectionTitle>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: "hidden" }}>
        {recurrenceByOrganization.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Nenhuma empresa recorrente identificada. Preencha o CNPJ dos contatos (campo na ficha do contato) para habilitar este agrupamento.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell align="right">Contatos</TableCell>
                <TableCell align="right">Chamados no período</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recurrenceByOrganization.map((o) => (
                <TableRow key={o.organizationId} hover>
                  <TableCell>{o.organizationName || "-"}</TableCell>
                  <TableCell>{o.documentNumber || "-"}</TableCell>
                  <TableCell align="right">{o.contactCount}</TableCell>
                  <TableCell align="right"><strong>{o.ticketCount}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CategoryIcon fontSize="small" color="action" />
        <SectionTitle>Motivos dos atendimentos</SectionTitle>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: "hidden" }}>
        {reasonBreakdown.every(r => r.reason === "Sem motivo registrado") ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Nenhum atendimento foi marcado com motivo ainda. Use o campo "Motivo do atendimento" no topo da conversa.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Motivo</TableCell>
                <TableCell align="right">Atendimentos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reasonBreakdown.map((r) => {
                const isExpanded = expandedReason === r.reason;
                const clients = contactsByReason[r.reason] || [];
                return (
                  <React.Fragment key={r.reason}>
                    <TableRow
                      hover
                      onClick={() => setExpandedReason(isExpanded ? null : r.reason)}
                      sx={{ cursor: "pointer", "& > *": { borderBottom: isExpanded ? "unset" : undefined } }}
                    >
                      <TableCell sx={{ width: 40 }}>
                        <IconButton size="small">
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.reason} sx={{ bgcolor: r.color, color: "#fff", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="right"><strong>{r.ticketCount}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0 }} colSpan={3}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {clients.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, px: 1 }}>
                              Nenhum cliente encontrado.
                            </Typography>
                          ) : (
                            <Table size="small" sx={{ my: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Cliente</TableCell>
                                  <TableCell>Número</TableCell>
                                  <TableCell align="right">Atendimentos</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {clients.map((c) => (
                                  <TableRow key={c.contactId}>
                                    <TableCell>{c.contactName || "-"}</TableCell>
                                    <TableCell>{c.contactNumber || "-"}</TableCell>
                                    <TableCell align="right">{c.ticketCount}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon fontSize="small" color="action" />
        <SectionTitle>Últimos atendimentos resolvidos (resumo IA)</SectionTitle>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, p: 2 }}>
        {(!recentClosedSummaries || recentClosedSummaries.length === 0) ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum atendimento encerrado com resumo de IA nesse período.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {recentClosedSummaries.map((s) => (
              <Paper key={s.ticketId} variant="outlined" sx={{ p: 1.5, flex: "1 1 260px", minWidth: 220 }}>
                <Typography variant="body2" fontWeight="bold">{s.contactName || "-"}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", mt: 0.5 }}>
                  {s.closingSummary}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <SectionTitle>Horário de pico (últimos 90 dias)</SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2.5, p: 2, overflowX: "auto" }}>
        {(!peakHours || peakHours.length === 0) ? (
          <Typography variant="body2" color="text.secondary">
            Ainda não há histórico suficiente pra mostrar um padrão de horário.
          </Typography>
        ) : (
          <PeakHoursHeatmap data={peakHours} />
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceReportTab;
